// src/components/ecommerce/ProductForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  X,
  Save,
  Upload,
  Trash2,
  Plus,
  Minus,
  Image as ImageIcon,
  Package,
  DollarSign,
  Tag,
  Info,
  Star,
  Clock,
  Globe,
  Zap,
  ArrowUpDown,
  AlertCircle,
  CheckCircle,
  Loader2,
  Camera,
  Grid,
  Type,
  Hash,
  FileText,
  Palette,
  Layers,
  Settings
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { firestoreService } from '../../services/firestoreService';
import { FileUpload } from '../common/FileUpload';
import { getStoreTypeConfig } from './utils/storeTypeConfig';

export const ProductForm = ({ 
  product, 
  storeId, 
  categories, 
  storeData,
  storeTypeConfig,
  isOpen, 
  onClose, 
  onSave 
}) => {
  const config = storeTypeConfig || getStoreTypeConfig(storeData?.storeType || 'general');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    salePrice: '',
    sku: '',
    category: '',
    subcategory: '',
    brand: '',
    tags: [],
    images: [],
    quantity: '',
    weight: '',
    dimensions: '',
    status: 'active',
    featured: false,
    digital: false,
    variants: [],
    specifications: {},
    features: [],
    seoTitle: '',
    seoDescription: '',
    seoKeywords: [],
    duration: '',
    bookingInstructions: '',
    serviceProcess: '',
    deliveryMethod: '',
    requirements: []
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [newTag, setNewTag] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newSpec, setNewSpec] = useState({ key: '', value: '' });

  const [uploadStates, setUploadStates] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const [availableSubcategories, setAvailableSubcategories] = useState([]);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        salePrice: product.salePrice?.toString() || '',
        sku: product.sku || '',
        category: product.category || '',
        subcategory:product.subcategory || '',
        brand: product.brand || '',
        tags: product.tags || [],
        images: product.images || [],
        quantity: product.quantity?.toString() || '',
        weight: product.weight?.toString() || '',
        dimensions: product.dimensions || '',
        status: product.status || 'active',
        featured: product.featured || false,
        digital: product.digital || false,
        variants: product.variants || [],
        specifications: product.specifications || {},
        features: product.features || [],
        seoTitle: product.seoTitle || '',
        seoDescription: product.seoDescription || '',
        seoKeywords: product.seoKeywords || []
      });
    } else {
      // Generate SKU for new product
      const sku = `PROD-${Date.now()}`;
      setFormData(prev => ({ ...prev, sku }));
    }
  }, [product]);

  useEffect(() => {
    if (formData.category) {
      const selectedCategory = categories.find(cat => cat.id === formData.category);
      setAvailableSubcategories(selectedCategory?.subcategories || []);
      
      // Reset subcategory if it doesn't exist in new category
      if (formData.subcategory && selectedCategory?.subcategories) {
        const subcategoryExists = selectedCategory.subcategories.some(
          sub => sub.id === formData.subcategory
        );
        if (!subcategoryExists) {
          setFormData(prev => ({ ...prev, subcategory: '' }));
        }
      }
    } else {
      setAvailableSubcategories([]);
      setFormData(prev => ({ ...prev, subcategory: '' }));
    }
  }, [formData.category, categories]);

  const clearFieldError = (field) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const validateField = (field, value) => {
    let error = null;
    
    switch (field) {
      case 'name':
        if (!value || !value.trim()) {
          error = 'Product name is required';
        }
        break;
      case 'price':
        if (!value || parseFloat(value) <= 0) {
          error = 'Valid price is required';
        }
        break;
      case 'category':
        if (!value) {
          error = 'Category is required';
        }
        break;
      case 'quantity':
        if (!value || parseInt(value) < 0) {
          error = 'Valid quantity is required';
        }
        break;
    }
    
    return error;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Real-time validation: clear error if field becomes valid
    const fieldError = validateField(field, value);
    
    if (fieldError) {
      setErrors(prev => ({ ...prev, [field]: fieldError }));
    } else {
      // Clear the error for this field if it's now valid
      clearFieldError(field);
    }
    
    if (field === 'category') {
      setFormData(prev => ({ ...prev, subcategory: '' }));
      setAvailableSubcategories([]);
    }
  };

  const isFormValid = () => {
    const requiredFields = ['name', 'price', 'category', 'quantity'];
    
    // Check if all required fields have values
    const hasRequiredValues = requiredFields.every(field => {
      const value = formData[field];
      return value && value.toString().trim() !== '';
    });
    
    // Check if there are no validation errors
    const hasNoErrors = Object.keys(errors).length === 0;
    
    return hasRequiredValues && hasNoErrors;
  };

  const handleImageUpload = async (files, error) => {
    if (error) {
      setValidationErrors(prev => ({ ...prev, images: error }));
      return;
    }

    if (!files) return;

    setUploadStates(prev => ({ ...prev, images: { uploading: true, error: null } }));

    try {
      // Handle both single file and multiple files
      const fileArray = Array.isArray(files) ? files : [files];
      
      const uploadPromises = fileArray.map(file => 
        firestoreService.uploadFile(file, `pb-${storeId}/products/images`)
      );
      
      const results = await Promise.all(uploadPromises);
      const imageUrls = results.map(result => result.url);
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...imageUrls]
      }));

      setUploadStates(prev => ({ ...prev, images: { uploading: false, error: null } }));
      
    } catch (error) {
      console.error('Error uploading product images:', error);
      setUploadStates(prev => ({ 
        ...prev, 
        images: { uploading: false, error: error.message } 
      }));
      setValidationErrors(prev => ({ ...prev, images: error.message }));
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addSpecification = () => {
    if (newSpec.key.trim() && newSpec.value.trim()) {
      setFormData(prev => ({
        ...prev,
        specifications: {
          ...prev.specifications,
          [newSpec.key.trim()]: newSpec.value.trim()
        }
      }));
      setNewSpec({ key: '', value: '' });
    }
  };

  const removeSpecification = (key) => {
    setFormData(prev => {
      const specs = { ...prev.specifications };
      delete specs[key];
      return { ...prev, specifications: specs };
    });
  };

  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, {
        type: 'size',
        name: '',
        sku: '',
        priceAdjustment: 0,
        stock: 0
      }]
    }));
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((variant, i) => 
        i === index ? { ...variant, [field]: value } : variant
      )
    }));
  };

  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.quantity || parseInt(formData.quantity) < 0) {
      newErrors.quantity = 'Valid quantity is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        salePrice: formData.salePrice ? parseFloat(formData.salePrice) : null,
        quantity: parseInt(formData.quantity),
        weight: formData.weight ? parseFloat(formData.weight) : null
      };

      if (product) {
        await ecommerceService.updateProduct(storeId, product.id, productData);
      } else {
        await ecommerceService.addProduct(storeId, productData);
      }

      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Info },
    { id: 'images', label: 'Images', icon: ImageIcon },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'inventory', label: 'Inventory', icon: Package },
    ...(config.showVariants ? [{ id: 'variants', label: 'Variants', icon: Layers }] : []),
    ...(config.showBookingButton ? [{ id: 'booking', label: 'Booking Info', icon: Clock }] : []),
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'seo', label: 'SEO', icon: Globe }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {product ? 'Edit Product' : 'Add New Product'}
            </h2>
            <p className="text-gray-600">
              {product ? 'Update product information' : 'Create a new product for your store'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{tab.label}</span>
                  {errors[tab.id] && (
                    <AlertCircle className="h-3 w-3 text-red-500" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {config.itemName} Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder={`Enter ${config.itemName.toLowerCase()} name`}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">SKU</label>
                  <input
                    type="text"
                    value={formData.sku}
                    onChange={(e) => handleInputChange('sku', e.target.value)}
                    placeholder="Auto-generated if empty"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  {config.descriptionLabel} *
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={config.itemName === 'Service' ? 6 : 4}
                  placeholder={config.descriptionPlaceholder}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {config.itemName === 'Service' && (
                  <p className="text-sm text-gray-500 mt-1">
                    💡 Provide detailed information about what this service includes, expected outcomes, duration, and the process customers can expect.
                  </p>
                )}
                {errors.description && (
                  <p className="text-red-600 text-sm mt-1">{errors.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.category ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                  )}
                </div>

                {/* Subcategory Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Subcategory
                    {availableSubcategories.length > 0 && (
                      <span className="text-gray-400 font-normal"> (Optional)</span>
                    )}
                  </label>
                  <select
                    value={formData.subcategory}
                    onChange={(e) => handleInputChange('subcategory', e.target.value)}
                    disabled={!formData.category || availableSubcategories.length === 0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {!formData.category 
                        ? 'Select a category first' 
                        : availableSubcategories.length === 0 
                          ? 'No subcategories available'
                          : 'All subcategories'
                      }
                    </option>
                    {availableSubcategories
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map(subcategory => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </option>
                      ))}
                  </select>
                  {formData.category && availableSubcategories.length === 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      No subcategories defined for this category
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Brand
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => handleInputChange('brand', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Brand name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Add a tag"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => handleInputChange('featured', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium">Featured Product</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.digital}
                    onChange={(e) => handleInputChange('digital', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium">Digital Product</span>
                </label>
              </div>
            </div>
          )}

          {activeTab === 'booking' && config.showBookingButton && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-blue-900 mb-2">Service Booking Information</h4>
                <p className="text-sm text-blue-700">
                  Provide clear instructions and details to help customers understand how to book and what to expect from this service.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  {config.instructionsLabel || 'Booking Instructions'} *
                </label>
                <textarea
                  value={formData.bookingInstructions || ''}
                  onChange={(e) => handleInputChange('bookingInstructions', e.target.value)}
                  rows={4}
                  placeholder={config.instructionsPlaceholder || 'Explain how customers can book or purchase this service...'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Include contact information, scheduling details, payment terms, or next steps
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Service Duration
                </label>
                <input
                  type="text"
                  value={formData.duration || ''}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  placeholder="e.g., 2 hours, 3 days, 1 week, 30 minutes"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  How long does this service typically take?
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Service Process/Steps
                </label>
                <textarea
                  value={formData.serviceProcess || ''}
                  onChange={(e) => handleInputChange('serviceProcess', e.target.value)}
                  rows={4}
                  placeholder="1. Initial consultation&#10;2. Assessment and planning&#10;3. Service delivery&#10;4. Follow-up and support"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Describe the step-by-step process of how this service is delivered
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Delivery Method
                </label>
                <select
                  value={formData.deliveryMethod || ''}
                  onChange={(e) => handleInputChange('deliveryMethod', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select delivery method</option>
                  <option value="in-person">In-Person</option>
                  <option value="remote">Remote/Online</option>
                  <option value="hybrid">Hybrid (In-Person & Remote)</option>
                  <option value="on-site">On-Site (At Customer Location)</option>
                  <option value="scheduled">Scheduled Appointment</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Customer Requirements
                </label>
                <div className="space-y-2">
                  {formData.requirements?.map((req, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={req}
                        onChange={(e) => {
                          const newReqs = [...formData.requirements];
                          newReqs[index] = e.target.value;
                          handleInputChange('requirements', newReqs);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="What customers need to provide or prepare"
                      />
                      <button
                        onClick={() => {
                          const newReqs = formData.requirements.filter((_, i) => i !== index);
                          handleInputChange('requirements', newReqs);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => handleInputChange('requirements', [...(formData.requirements || []), ''])}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Add Requirement
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  List any materials, information, or preparations customers need before booking
                </p>
              </div>
            </div>
          )}

          {activeTab === 'images' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Images
                </label>
                <p className="text-sm text-gray-600 mb-4">
                  Upload product images. Drag images to reorder them. The first image will be the main product image.
                </p>

                {/* Upload Area */}
                <div className="mb-6">
                  <FileUpload
                    onFileSelect={handleImageUpload}
                    accept="image/*"
                    maxSize={10}
                    label="Upload Product Images"
                    uploading={uploadStates.images?.uploading || false}
                    error={uploadStates.images?.error || validationErrors.images}
                    showPreview={false}
                    multiple={true}
                  />
                </div>

                {/* Image Management Grid */}
                {formData.images.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-700">
                        Product Images ({formData.images.length})
                      </h4>
                      <div className="text-xs text-gray-500">
                        Drag images to reorder • First image is main image
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {formData.images.map((imageUrl, index) => (
                        <div
                          key={index}
                          className="relative group bg-gray-100 rounded-lg overflow-hidden aspect-square cursor-move"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('text/plain', index.toString());
                          }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                            const newImages = [...formData.images];
                            const draggedImage = newImages.splice(draggedIndex, 1)[0];
                            newImages.splice(index, 0, draggedImage);
                            setFormData(prev => ({ ...prev, images: newImages }));
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={`Product ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          
                          {/* Image Overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                            <button
                              onClick={() => removeImage(index)}
                              className="opacity-0 group-hover:opacity-100 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-all duration-200"
                              title="Remove image"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>

                          {/* Image Position Badge */}
                          <div className="absolute top-2 left-2">
                            {index === 0 ? (
                              <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded font-medium">
                                Main
                              </span>
                            ) : (
                              <span className="bg-gray-800 bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                                #{index + 1}
                              </span>
                            )}
                          </div>

                          {/* Move Handle */}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="bg-gray-800 bg-opacity-75 text-white p-1 rounded cursor-move">
                              <ArrowUpDown className="h-3 w-3" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Image Actions */}
                    {formData.images.length > 1 && (
                      <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
                        <button
                          onClick={() => {
                            const newImages = [...formData.images].reverse();
                            setFormData(prev => ({ ...prev, images: newImages }));
                          }}
                          className="flex items-center gap-1 hover:text-gray-900"
                        >
                          <ArrowUpDown className="h-4 w-4" />
                          Reverse Order
                        </button>
                        
                        <button
                          onClick={() => {
                            if (window.confirm('Remove all images? This action cannot be undone.')) {
                              setFormData(prev => ({ ...prev, images: [] }));
                            }
                          }}
                          className="flex items-center gap-1 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                          Clear All
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Guidelines */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h5 className="text-sm font-medium text-blue-900 mb-2">Image Guidelines</h5>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Use high-quality images (1200x1200px or larger recommended)</li>
                    <li>• First image will be the main product image shown in listings</li>
                    <li>• Include multiple angles and close-up details</li>
                    <li>• Maximum file size: 10MB per image</li>
                    <li>• Supported formats: JPG, PNG, WebP</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'pricing' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Regular Price *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      R
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.price ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                    />
                  </div>
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Sale Price
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                      R
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.salePrice}
                      onChange={(e) => handleInputChange('salePrice', e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Leave empty if not on sale
                  </p>
                </div>
              </div>

              {/* Price Preview */}
              {formData.price && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-3">Price Preview</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-gray-900">
                      R{(formData.salePrice || formData.price || 0)}
                    </span>
                    {formData.salePrice && formData.salePrice < formData.price && (
                      <>
                        <span className="text-lg text-gray-500 line-through">
                          R{formData.price}
                        </span>
                        <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-sm font-medium">
                          {Math.round(((formData.price - formData.salePrice) / formData.price) * 100)}% OFF
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'inventory' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Stock Quantity *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.quantity ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0"
                  />
                  {errors.quantity && (
                    <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Weight (kg)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.weight}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Dimensions (L×W×H cm)
                  </label>
                  <input
                    type="text"
                    value={formData.dimensions}
                    onChange={(e) => handleInputChange('dimensions', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="30×20×10"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Continue with other tabs... */}
          {/* For brevity, I'll add the key parts */}

          {activeTab === 'variants' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Product Variants</h3>
                  <p className="text-sm text-gray-600">
                    Add different options like size, color, etc.
                  </p>
                </div>
                <button
                  onClick={addVariant}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Add Variant
                </button>
              </div>

              {formData.variants.map((variant, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium">Variant {index + 1}</h4>
                    <button
                      onClick={() => removeVariant(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Type</label>
                      <select
                        value={variant.type}
                        onChange={(e) => updateVariant(index, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="size">Size</option>
                        <option value="color">Color</option>
                        <option value="material">Material</option>
                        <option value="style">Style</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Name</label>
                      <input
                        type="text"
                        value={variant.name}
                        onChange={(e) => updateVariant(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Large, Red"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Price Adj.</label>
                      <input
                        type="number"
                        step="0.01"
                        value={variant.priceAdjustment}
                        onChange={(e) => updateVariant(index, 'priceAdjustment', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Stock</label>
                      <input
                        type="number"
                        min="0"
                        value={variant.stock}
                        onChange={(e) => updateVariant(index, 'stock', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {formData.variants.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Layers className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No variants added yet</p>
                  <p className="text-sm">Add variants to offer different options</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Features */}
              <div>
                <label className="block text-sm font-medium mb-2">Features</label>
                <div className="space-y-2 mb-3">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="flex-1">{feature}</span>
                      <button
                        onClick={() => removeFeature(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addFeature()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Add a feature"
                  />
                  <button
                    onClick={addFeature}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Specifications */}
              <div>
                <label className="block text-sm font-medium mb-2">Specifications</label>
                <div className="space-y-2 mb-3">
                  {Object.entries(formData.specifications).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <span className="font-medium">{key}:</span>
                      <span className="flex-1">{value}</span>
                      <button
                        onClick={() => removeSpecification(key)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={newSpec.key}
                    onChange={(e) => setNewSpec(prev => ({ ...prev, key: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Specification name"
                  />
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newSpec.value}
                      onChange={(e) => setNewSpec(prev => ({ ...prev, value: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Value"
                    />
                    <button
                      onClick={addSpecification}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'seo' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">SEO Title</label>
                <input
                  type="text"
                  value={formData.seoTitle}
                  onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Product name - Your Store"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.seoTitle.length}/60 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">SEO Description</label>
                <textarea
                  value={formData.seoDescription}
                  onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description for search engines"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.seoDescription.length}/160 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">SEO Keywords</label>
                <input
                  type="text"
                  value={formData.seoKeywords.join(', ')}
                  onChange={(e) => handleInputChange('seoKeywords', e.target.value.split(',').map(k => k.trim()))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {Object.keys(errors).length > 0 && (
              <span className="text-red-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                Please fix the errors above
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !isFormValid()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-200"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? 'Saving...' : (product ? 'Update Product' : 'Create Product')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};